import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useConnectivity } from '../contexts/ConnectivityContext';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Divider,
  useTheme
} from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import TwoFactorInput from '../components/TwoFactorInput';
import GoogleSignInButton from '../components/GoogleSignInButton';
import PasswordInput from '../components/PasswordInput';
import { authApi } from '../services/api';
import { getPendingLoginCredentials, setPendingLoginCredentials, hasPendingLoginCredentials, clearPendingLoginCredentials } from '../utils/pendingLoginCredentials';

const Login = () => {
  const [formData, setFormData] = useState(() => {
    // Initialize from the in-memory pending-credentials store, to persist
    // across unmounts within this session (see pendingLoginCredentials.js
    // for why this isn't localStorage).
    const savedData = getPendingLoginCredentials();
    if (savedData) {
      return {
        identifier: savedData.identifier || '',
        password: savedData.password || ''
      };
    }
    return {
      identifier: '',
      password: ''
    };
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(
    localStorage.getItem('keepSignedIn') === 'true'
  );
  const [error, setError] = useState('');
  const [googleError, setGoogleError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [requires2FA, setRequires2FA] = useState(() => {
    // Initialize from localStorage to persist across unmounts
    return localStorage.getItem('requires2FA') === 'true';
  });
  const [twoFactorError, setTwoFactorError] = useState('');
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
  const [emailVerificationData, setEmailVerificationData] = useState(null);
  const [requiresGoogle2FA, setRequiresGoogle2FA] = useState(false);
  const [google2FAData, setGoogle2FAData] = useState(null);

  const { login, googleLogin, googleLogin2FA, updateInactivityTimeout, resendVerificationEmail, currentUser } = useAuth();
  const { isOnline, checkServerConnectivity } = useConnectivity();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  // Helper function to calculate minutes until midnight
  const getMinutesUntilMidnight = () => {
    const now = new Date();
    const midnight = new Date();
    midnight.setHours(24, 0, 0, 0);
    return Math.ceil((midnight - now) / (1000 * 60));
  };

  // Check connectivity when component mounts and when there's an error
  useEffect(() => {
    checkServerConnectivity();
  }, [error]);

  // Load saved identifier on component mount
  useEffect(() => {
    const savedIdentifier = localStorage.getItem('rememberedIdentifier');
    if (savedIdentifier) {
      setFormData(prev => ({...prev, identifier: savedIdentifier}));
      setRememberMe(true);
    }
  }, []);

  // Listen for changes to keepSignedIn setting from Settings page
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'keepSignedIn') {
        setKeepSignedIn(e.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Navigate to dashboard when user becomes authenticated
  useEffect(() => {
    if (currentUser && !requiresGoogle2FA) {
      console.log('🔍 Login navigating to dashboard because user is authenticated');
      navigate('/dashboard');
    } else if (currentUser && requiresGoogle2FA) {
      console.log('🔍 Login user authenticated but in Google 2FA flow, not navigating');
    }
  }, [currentUser, navigate, requiresGoogle2FA]);

  // Check for Google 2FA state from navigation (when redirected from Landing page)
  useEffect(() => {
    if (location.state?.requiresGoogle2FA && location.state?.google2FAData) {
      console.log('🔍 Login received Google 2FA state from navigation:', location.state);
      setRequiresGoogle2FA(true);
      setGoogle2FAData(location.state.google2FAData);
      // Clear the navigation state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Clear 2FA state when user is already authenticated or when component mounts
  useEffect(() => {
    if (currentUser) {
      localStorage.removeItem('requires2FA');
      clearPendingLoginCredentials();
      setRequires2FA(false);
    }
  }, [currentUser]);

  // Clear 2FA state on component mount if user is not authenticated
  useEffect(() => {
    if (!currentUser && requires2FA) {
      // Check if we should clear the 2FA state (e.g., if user navigated away and came back)
      const shouldClear = !hasPendingLoginCredentials();
      if (shouldClear) {
        localStorage.removeItem('requires2FA');
        setRequires2FA(false);
      }
    }
  }, [currentUser, requires2FA]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({...prev, [name]: value}));
  };

  const handleRememberMeChange = (e) => {
    setRememberMe(e.target.checked);
  };

  const handleKeepSignedInChange = (e) => {
    const newValue = e.target.checked;
    setKeepSignedIn(newValue);
    
    // Save to localStorage immediately
    if (newValue) {
      localStorage.setItem('keepSignedIn', 'true');
    } else {
      localStorage.removeItem('keepSignedIn');
    }
    
    // Dispatch storage event to notify other components (like Settings) of the change
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'keepSignedIn',
      newValue: newValue ? 'true' : null,
      oldValue: newValue ? null : 'true'
    }));
  };

  const handle2FASubmit = async (twoFactorCode, trustDevice = false) => {
    console.log('🔍 Login handle2FASubmit called with code:', twoFactorCode);
    try {
      setTwoFactorError('');
      setLoading(true);
      
      // Submit login with 2FA code
      const loginData = {
        identifier: formData.identifier,
        password: formData.password,
        two_fa_code: twoFactorCode
      };
      
      // If "Keep signed in" is checked, add the expiration time
      if (keepSignedIn) {
        const midnight = new Date();
        midnight.setHours(24, 0, 0, 0);
        loginData.expires_at = midnight.getTime();
      }
      
      console.log('🔍 Login submitting 2FA with data:', { ...loginData, password: '***', two_fa_code: '***' });
      
      // Use the auth context login method which will handle the API call
      const loginResponse = await login(loginData.identifier, loginData.password, loginData.expires_at, twoFactorCode);
      
      console.log('🔍 Login 2FA login response:', loginResponse);
      
      // Check if login was successful (has access_token)
      if (loginResponse && loginResponse.access_token) {
        console.log('🔍 Login 2FA login successful, navigating to dashboard');
        // If user wants to trust this device, make the trust device request.
        // This call (and storing the returned token) was commented out here
        // while the rest of the flow - including the "don't challenge me
        // for 30 days on this computer" checkbox in TwoFactorInput.js that
        // sets trustDevice - shipped and worked normally, so every user who
        // logged in via this page and checked that box was still prompted
        // for a 2FA code on their next login. LandingPage.js's equivalent
        // 2FA handler already calls this correctly.
        if (trustDevice) {
          try {
            const deviceName = `${navigator.platform} - ${navigator.userAgent.split(' ').pop()}`;
            const trustResponse = await authApi.trustDevice(twoFactorCode, deviceName);

            if (trustResponse.data.device_token) {
              localStorage.setItem('trustedDeviceToken', trustResponse.data.device_token);
            }
          } catch (trustError) {
            // Don't fail the login if device trust fails
          }
        }
        
        // Handle timeout settings
        if (keepSignedIn) {
          const minutesUntilMidnight = getMinutesUntilMidnight();
          updateInactivityTimeout(minutesUntilMidnight);
          localStorage.setItem('keepSignedIn', 'true');
          localStorage.setItem('signInExpiryTime', loginData.expires_at.toString());
        } else {
          updateInactivityTimeout(30);
          localStorage.removeItem('keepSignedIn');
          localStorage.removeItem('signInExpiryTime');
        }
        
        // Clear 2FA state and navigate to dashboard
        localStorage.removeItem('requires2FA');
        clearPendingLoginCredentials();
        navigate('/dashboard');
      } else {
        // If no access_token, it might be an error or unexpected response
        setTwoFactorError('Login failed. Please try again.');
      }

    } catch (err) {
      if (!err.response || err.code === 'ECONNABORTED' || err.message === 'Network Error') {
        setTwoFactorError('Network error: Please check your internet connection');
      } else {
        const errorMessage = err.response?.data?.error || 'Invalid verification code';
        setTwoFactorError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setRequires2FA(false);
    setTwoFactorError('');
    localStorage.removeItem('requires2FA');
    clearPendingLoginCredentials();
  };

  const handleGoogle2FASubmit = async (twoFactorCode, trustDevice = false) => {
    console.log('🔍 Login handleGoogle2FASubmit called with code:', twoFactorCode);
    try {
      setTwoFactorError('');
      setLoading(true);

      const deviceName = trustDevice ? `${navigator.platform} - ${navigator.userAgent.split(' ').pop()}` : null;

      console.log('🔍 Login submitting Google 2FA with userId:', google2FAData?.user_id);

      // Use the auth context googleLogin2FA method
      const loginResponse = await googleLogin2FA(google2FAData?.user_id, twoFactorCode, trustDevice, deviceName);

      console.log('🔍 Login Google 2FA login response:', loginResponse);

      // Check if login was successful (has access_token)
      if (loginResponse && loginResponse.access_token) {
        console.log('🔍 Login Google 2FA login successful, navigating to dashboard');

        // Clear 2FA state and navigate to dashboard
        setRequiresGoogle2FA(false);
        setGoogle2FAData(null);
        navigate('/dashboard');
      } else {
        setTwoFactorError('Login failed. Please try again.');
      }

    } catch (err) {
      console.log('🔍 Login Google 2FA error:', err);
      if (!err.response || err.code === 'ECONNABORTED' || err.message === 'Network Error') {
        setTwoFactorError('Network error: Please check your internet connection');
      } else {
        const errorMessage = err.response?.data?.error || 'Invalid verification code';
        setTwoFactorError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToGoogleLogin = () => {
    setRequiresGoogle2FA(false);
    setGoogle2FAData(null);
    setTwoFactorError('');
  };

  const handleGoogleSuccess = async (googleToken) => {
    console.log('🔍 Login handleGoogleSuccess CALLED with token:', googleToken?.substring?.(0, 20) + '...');
    try {
      setError('');
      setGoogleError('');
      setLoading(true);

      console.log('🔍 Login calling googleLogin with token');
      const loginResponse = await googleLogin(googleToken);
      console.log('🔍 Login received response:', loginResponse);

      // Check if Google login requires 2FA
      if (loginResponse && loginResponse.requires_2fa) {
        console.log('🔍 Login Google login requires 2FA, showing 2FA interface');
        console.log('🔍 Login 2FA data:', {
          user_id: loginResponse.user_id,
          is_google_user: loginResponse.is_google_user
        });
        setRequiresGoogle2FA(true);
        setGoogle2FAData({
          user_id: loginResponse.user_id,
          is_google_user: loginResponse.is_google_user
        });
        console.log('🔍 Login state set - requiresGoogle2FA: true, google2FAData:', {
          user_id: loginResponse.user_id,
          is_google_user: loginResponse.is_google_user
        });
        setLoading(false);
        return;
      }

      // If we get here, login was successful (handled by auth context)
      console.log('🔍 Login Google login completed successfully');

    } catch (err) {
      console.log('🔍 Login handleGoogleSuccess ERROR:', err);
      console.log('🔍 Login handleGoogleSuccess ERROR details:', {
        response: err.response,
        message: err.message,
        code: err.code
      });

      if (!err.response || err.code === 'ECONNABORTED' || err.message === 'Network Error') {
        setIsNetworkError(true);
        setGoogleError('Network error: Please check your internet connection');
      } else {
        setIsNetworkError(false);
        setGoogleError(err.response?.data?.error || 'Google login failed');
      }
    } finally {
      console.log('🔍 Login handleGoogleSuccess FINALLY block');
      setLoading(false);
    }
  };

  const handleGoogleError = (error) => {
    console.log('🔍 Login handleGoogleError called:', error);
    setGoogleError(error || 'Google sign-in failed');
  };

  const handleResendVerification = async () => {
    if (!emailVerificationData?.email) return;
    
    try {
      setLoading(true);
      const result = await resendVerificationEmail(emailVerificationData.email);
      
      if (result.success) {
        // Navigate to verification pending page
        navigate('/verify-email-pending', {
          state: {
            email: emailVerificationData.email,
            emailSent: result.emailSent,
            fromLogin: true
          }
        });
      } else {
        setError(result.message || 'Failed to resend verification email');
      }
    } catch (err) {
      setError('Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    console.log('🔍 Login handleSubmit called');
    e.preventDefault();
    
    if (!isOnline) {
      // setError('No internet connection. Please check your network and try again.');
      // return;
    }
    
    try {
      setError('');
      setGoogleError('');
      setLoading(true);
      setIsNetworkError(false);
      
      // Save identifier if "Remember me" is checked
      if (rememberMe) {
        localStorage.setItem('rememberedIdentifier', formData.identifier);
      } else {
        localStorage.removeItem('rememberedIdentifier');
      }
      
      // Prepare login data
      const loginData = {
        identifier: formData.identifier,
        password: formData.password
      };
      
      // If "Keep signed in" is checked, add the expiration time
      if (keepSignedIn) {
        const midnight = new Date();
        midnight.setHours(24, 0, 0, 0);
        loginData.expires_at = midnight.getTime();
      }
      
      console.log('🔍 Login attempting login with data:', { ...loginData, password: '***' });
      
      // Attempt login
      const loginResponse = await login(loginData.identifier, loginData.password, loginData.expires_at);
      
      console.log('🔍 Login login response:', loginResponse);
      
      // Check if 2FA is required
      if (loginResponse && loginResponse.requires_2fa) {
        console.log('🔍 Login 2FA required, showing 2FA screen');
        console.log('🔍 Login requires2FA state before:', requires2FA);
        // Persist across unmounts (requires2FA in localStorage, since it's
        // not sensitive; the credentials themselves in-memory only - see
        // pendingLoginCredentials.js)
        localStorage.setItem('requires2FA', 'true');
        setPendingLoginCredentials(formData.identifier, formData.password);

        setRequires2FA(true);
        setLoading(false);
        return;
      }
      
      // Check if email verification is required
      if (loginResponse && loginResponse.email_verification_required) {
        setEmailVerificationRequired(true);
        setEmailVerificationData({
          email: formData.identifier,
          canResendVerification: loginResponse.can_resend_verification || false,
          emailResent: false
        });
        setLoading(false);
        return;
      }
      
      // Handle timeout settings
      if (keepSignedIn) {
        const minutesUntilMidnight = getMinutesUntilMidnight();
        updateInactivityTimeout(minutesUntilMidnight);
        localStorage.setItem('keepSignedIn', 'true');
        localStorage.setItem('signInExpiryTime', loginData.expires_at.toString());
      } else {
        updateInactivityTimeout(30);
        localStorage.removeItem('keepSignedIn');
        localStorage.removeItem('signInExpiryTime');
      }
      
      // Immediately navigate to dashboard after successful login
      navigate('/dashboard');
      
    } catch (err) {
      console.log('🔍 Login handleSubmit error:', err);
      console.log('🔍 Login handleSubmit err.response:', err.response);
      console.log('🔍 Login handleSubmit err.response?.data:', err.response?.data);
      
      if (!err.response || err.code === 'ECONNABORTED' || err.message === 'Network Error') {
        setIsNetworkError(true);
        setError('Network error: Please check your internet connection');
      } else {
        setIsNetworkError(false);
        const errorMessage = err.response?.data?.error || err.error || err.message || 'Login failed';
        console.log('🔍 Login handleSubmit errorMessage:', errorMessage);
        setError(errorMessage);
        console.log('🔍 Login error state set to:', errorMessage);
        
        // Handle specific error cases
        if (errorMessage.includes('email verification')) {
          setEmailVerificationRequired(true);
          setEmailVerificationData({
            email: formData.identifier,
            canResendVerification: err.response?.data?.can_resend_verification || false,
            emailResent: false
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Show Google 2FA screen if required
  console.log('🔍 Login render - requiresGoogle2FA:', requiresGoogle2FA, 'google2FAData:', google2FAData);
  if (requiresGoogle2FA) {
    console.log('🔍 Login rendering Google 2FA interface');
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom align="center">
              Two-Factor Authentication
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, textAlign: 'center' }}>
              Google Sign-In successful! 🎉
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
              Please enter the 6-digit code from your authenticator app to complete the login.
            </Typography>

            {twoFactorError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {twoFactorError}
              </Alert>
            )}

            <TwoFactorInput
              onSubmit={handleGoogle2FASubmit}
              onBack={handleBackToGoogleLogin}
              loading={loading}
              showTrustDevice={true}
            />
          </Paper>
        </Box>
      </Container>
    );
  }

  // Show 2FA screen if required
  if (requires2FA) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom align="center">
              Two-Factor Authentication
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
              Please enter the 6-digit code from your authenticator app.
            </Typography>
            
            {twoFactorError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {twoFactorError}
              </Alert>
            )}
            
            <TwoFactorInput
              onSubmit={handle2FASubmit}
              onBack={handleBackToLogin}
              loading={loading}
              showTrustDevice={true}
            />
          </Paper>
        </Box>
      </Container>
    );
  }

  // Show email verification screen if required
  if (emailVerificationRequired) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom align="center">
              Email Verification Required
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
              Please check your email at <strong>{emailVerificationData.email}</strong> and click the verification link.
            </Typography>
            
            {emailVerificationData.emailResent && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Verification email has been resent successfully.
              </Alert>
            )}
            
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                onClick={handleResendVerification}
                disabled={loading || !emailVerificationData.canResendVerification}
                sx={{ mr: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Resend Email'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleBackToLogin}
                disabled={loading}
              >
                Back to Login
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Container maxWidth="sm">
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            borderRadius: 2,
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            color: 'white'
          }}
        >
          {/* Logo and title in login form */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            mb: 3,
            gap: 2
          }}>
            <img 
              src="/logo.svg" 
              alt="Expense Tracker Logo" 
              style={{ 
                width: '60px', 
                height: '60px',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
              }}
            />
            <Typography variant="h4" component="h2" sx={{ color: 'white', fontWeight: 'bold' }}>
              Expense Tracker
            </Typography>
          </Box>
          
          {!isOnline && (
            <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.1)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WifiOffIcon />
                <Typography variant="body2">
                  No internet connection. Please check your network.
                </Typography>
              </Box>
            </Alert>
          )}
          
          {error && error !== 'trial_expired' && (
            <Alert 
              severity="error" 
              sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.1)' }}
              onClose={() => setError('')}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                {error}
              </Typography>
              {(error.includes('Incorrect password') || error.includes('No account found')) && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                  {error.includes('Incorrect password') && '💡 Tip: Check if Caps Lock is on, or try resetting your password.'}
                  {error.includes('No account found') && '💡 Tip: Try using your email address instead of username, or sign up for a new account.'}
                </Typography>
              )}
            </Alert>
          )}
          
          {googleError && !googleError.includes('development environment') && (
            <Alert 
              severity="warning" 
              sx={{ mb: 2, bgcolor: 'rgba(255,193,7,0.1)' }}
              onClose={() => setGoogleError('')}
            >
              <Typography variant="body2">
                {googleError}
              </Typography>
            </Alert>
          )}
          
          {emailVerificationRequired && (
            <Alert 
              severity="warning" 
              sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.1)' }}
            >
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
                📧 Email Verification Required
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Please check your email and click the verification link to activate your account.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {emailVerificationData?.canResendVerification && !emailVerificationData?.emailResent && (
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={handleResendVerification}
                    disabled={loading}
                    sx={{ minWidth: '140px' }}
                  >
                    {loading ? <CircularProgress size={20} /> : 'Resend Email'}
                  </Button>
                )}
                <Button 
                  variant="outlined" 
                  color="primary"
                  onClick={() => navigate('/verify-email-pending', {
                    state: {
                      email: emailVerificationData?.email,
                      emailSent: emailVerificationData?.emailResent || true,
                      fromLogin: true
                    }
                  })}
                  sx={{ minWidth: '140px' }}
                >
                  Need Help?
                </Button>
                <Button 
                  variant="outlined" 
                  color="primary"
                  onClick={() => setError('')}
                  sx={{ minWidth: '100px' }}
                >
                  Try Again
                </Button>
              </Box>
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="identifier"
              label="Username or Email"
              name="identifier"
              autoComplete="username email"
              value={formData.identifier}
              onChange={handleInputChange}
              autoFocus={!formData.identifier}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.9)',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,1)',
                  },
                  '& .MuiInputBase-input': {
                    color: theme.palette.mode === 'dark' ? 'white' : 'black',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                  '&.Mui-focused': {
                    color: theme.palette.mode === 'dark' ? 'white' : 'black',
                  },
                },
                '& .MuiInputLabel-shrink': {
                  transform: 'translate(14px, -20px) scale(0.75)',
                },
              }}
            />
            <PasswordInput
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleInputChange}
              disabled={loading}
              showStrengthIndicator={false}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.9)',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,1)',
                  },
                  '& .MuiInputBase-input': {
                    color: theme.palette.mode === 'dark' ? 'white' : 'black',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                  '&.Mui-focused': {
                    color: theme.palette.mode === 'dark' ? 'white' : 'black',
                  },
                },
                '& .MuiInputLabel-shrink': {
                  transform: 'translate(14px, -20px) scale(0.75)',
                },
              }}
            />
            
            <FormControlLabel
              control={
                <Checkbox 
                  checked={rememberMe} 
                  onChange={handleRememberMeChange}
                  color="primary"
                  disabled={loading}
                />
              }
              label="Remember my username/email"
              sx={{ mt: 1, color: 'white' }}
            />
            
            <FormControlLabel
              control={
                <Checkbox 
                  checked={keepSignedIn} 
                  onChange={handleKeepSignedInChange}
                  color="primary"
                  disabled={loading}
                />
              }
              label="Keep me signed in until midnight (remember this preference)"
              sx={{ mt: 1, color: 'white' }}
            />
            {keepSignedIn && (
              <Typography variant="caption" sx={{ 
                display: 'block', 
                mt: 0.5, 
                color: 'rgba(255,255,255,0.8)',
                fontStyle: 'italic'
              }}>
                ✓ Your session will stay active until midnight today (preference will persist)
              </Typography>
            )}
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              onClick={() => console.log('Button clicked!')}
              sx={{ 
                mt: 3, 
                mb: 2,
                bgcolor: 'white',
                color: '#1976d2',
                '&:hover': {
                  bgcolor: 'grey.100',
                }
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Log In'}
            </Button>
            
            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" sx={{ color: 'white' }}>
                or
              </Typography>
            </Divider>
            
            <GoogleSignInButton 
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              disabled={loading}
            />
            
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ mb: 1, color: 'white' }}>
                <Link to="/forgot-password" style={{ color: 'white', textDecoration: 'underline' }}>
                  Forgot password?
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'white' }}>
                <Link to="/" style={{ color: 'white', textDecoration: 'underline' }}>
                  Back to landing page
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ color: 'white' }}>
                Don't have an account?{' '}
                <Link to="/register" style={{ color: 'white', textDecoration: 'underline' }}>
                  Sign Up
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login; 